import React, { useState, useEffect, useCallback } from 'react';
import { useAvailableSlots, useTenantViewings } from '../hooks/useViewings';
import { formatTime, formatDate, getAvailableDates } from '../services/viewings.service';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  X, 
  Check,
  AlertCircle,
  UserPlus,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Modal voor het aanvragen van een bezichtiging
 * @param {Object} props
 * @param {boolean} props.isOpen - Is modal open
 * @param {Function} props.onClose - Close callback
 * @param {Object} props.listing - Property data
 * @param {string} props.listingId - Property ID
 * @param {string} props.landlordUid - Landlord user ID
 */
export default function ViewingRequestModal({ 
  isOpen, 
  onClose, 
  listing, 
  listingId, 
  landlordUid 
}) {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [coTenants, setCoTenants] = useState([]);
  const [coTenantEmail, setCoTenantEmail] = useState('');
  const [searchingCoTenant, setSearchingCoTenant] = useState(false);

  const { slots, loading: slotsLoading, error: slotsError } = useAvailableSlots(
    landlordUid, 
    listingId,
    selectedDate
  );

  const { actions: { requestViewing } } = useTenantViewings();

  // Load available dates when modal opens
  const loadAvailableDates = useCallback(async () => {
    if (!listingId) {
      setAvailableDates([]);
      setLoadingDates(false);
      setSelectedDate(null);
      return;
    }
    
    setLoadingDates(true);
    try {
      const dates = await getAvailableDates(listingId, 30); // 30 dagen vooruit
      setAvailableDates(dates || []);
      
      // Selecteer eerste beschikbare datum
      if (dates && dates.length > 0) {
        setSelectedDate(dates[0]);
      } else {
        setSelectedDate(null);
      }
    } catch (error) {
      console.error('Error loading available dates:', error);
      setAvailableDates([]);
      setSelectedDate(null);
    } finally {
      setLoadingDates(false);
    }
  }, [listingId]);

  useEffect(() => {
    if (isOpen && listingId) {
      loadAvailableDates();
      setSelectedSlot(null);
      setNotes('');
      setCoTenants([]);
      setCoTenantEmail('');
    }
  }, [isOpen, listingId, loadAvailableDates]);

  const searchCoTenantByEmail = async (email) => {
    if (!email || !email.includes('@')) {
      toast.error('Voer een geldig email adres in');
      return;
    }

    if (coTenants.some(ct => ct.email?.toLowerCase() === email.toLowerCase())) {
      toast.error('Deze persoon is al toegevoegd');
      return;
    }

    setSearchingCoTenant(true);
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email.toLowerCase().trim())
      );
      const snapshot = await getDocs(usersQuery);
      
      if (snapshot.docs.length > 0) {
        const userData = snapshot.docs[0].data();
        const userId = snapshot.docs[0].id;
        
        // Check if it's the current user
        if (userId === user?.uid) {
          toast.error('Je kunt jezelf niet toevoegen');
          setSearchingCoTenant(false);
          return;
        }

        // Check if it's a tenant
        const userType = userData.userType || userData.accountType || userData.type || userData.role;
        const userTypeLower = userType ? userType.toLowerCase() : '';
        const isLandlord = userTypeLower === 'landlord' || userTypeLower === 'verhuurder';
        
        if (isLandlord) {
          toast.error('Verhuurders kunnen niet als mede-huurder worden toegevoegd');
          setSearchingCoTenant(false);
          return;
        }

        const coTenant = {
          id: userId,
          email: userData.email,
          displayName: userData.displayName || userData.name || email,
          name: userData.displayName || userData.name || email
        };

        setCoTenants([...coTenants, coTenant]);
        setCoTenantEmail('');
        toast.success(`${coTenant.displayName} toegevoegd`);
      } else {
        toast.error('Geen gebruiker gevonden met dit email adres');
      }
    } catch (error) {
      console.error('Error searching co-tenant:', error);
      toast.error('Fout bij zoeken naar gebruiker');
    } finally {
      setSearchingCoTenant(false);
    }
  };

  const removeCoTenant = (id) => {
    setCoTenants(coTenants.filter(ct => ct.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast.error('Selecteer een datum');
      return;
    }
    
    if (!selectedSlot) {
      toast.error('Selecteer een tijdstip');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const agencyHandoverStatus = listing?.management?.handoverStatus || 'active';
      const hasActiveAgency =
        Boolean(listing?.management?.agencyId) &&
        ['active', 'requested'].includes(agencyHandoverStatus);

      const effectiveLandlordUid = hasActiveAgency
        ? listing?.management?.agencyId
        : listing?.ownerId ||
          listing?.verhuurderID ||
          listing?.landlordId ||
          listing?.landlordID ||
          listing?.management?.agencyId ||
          listing?.createdByAgencyId ||
          landlordUid;

      await requestViewing({
        listingId,
        landlordUid: effectiveLandlordUid,
        startAt: selectedSlot.startAt,
        endAt: selectedSlot.endAt,
        location: listing?.adres || listing?.address || '',
        notes: notes.trim(),
        coTenants: coTenants.map(ct => ct.id) // Array van user IDs
      });

      toast.success('Bezichtiging aangevraagd!');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Fout bij aanvragen van bezichtiging');
    } finally {
      setIsSubmitting(false);
    }
  };


  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString('nl-NL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Bezichtiging plannen</h2>
            <p className="text-sm text-gray-600 mt-1">
              {listing?.titel || listing?.adres || 'Property'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Property Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">
                  {listing?.titel || listing?.adres || 'Property'}
                </h3>
                <p className="text-sm text-gray-600">
                  {listing?.adres || listing?.address || ''}
                </p>
                {listing?.stad && (
                  <p className="text-sm text-gray-500">{listing.stad}</p>
                )}
              </div>
            </div>
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Selecteer datum
            </label>
            {loadingDates ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ios-blue"></div>
                <span className="ml-2 text-gray-600 text-sm">Laden beschikbare datums...</span>
              </div>
            ) : availableDates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>Geen beschikbare datums voor bezichtigingen</p>
                <p className="text-sm mt-1">De verhuurder heeft nog geen tijdslots ingesteld</p>
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-2">
                {availableDates.map((date) => (
                  <button
                    key={date.toISOString()}
                    type="button"
                    onClick={() => {
                      setSelectedDate(date);
                      setSelectedSlot(null); // Reset slot bij datum wijziging
                    }}
                    className={`p-3 text-center rounded-lg border transition-colors ${
                      selectedDate && selectedDate.toDateString() === date.toDateString()
                        ? 'bg-ios-blue text-white border-ios-blue'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-xs font-medium">
                      {formatDateDisplay(date)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Time Slots */}
          {selectedDate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Beschikbare tijdstippen
              </label>
              
              {slotsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ios-blue"></div>
                <span className="ml-2 text-gray-600">Laden...</span>
              </div>
            ) : slotsError ? (
              <div className="flex items-center justify-center py-8 text-red-600">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span>Fout bij laden van beschikbare tijden: {slotsError}</span>
              </div>
            ) : slots.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>Geen beschikbare tijdstippen op deze datum</p>
                <p className="text-sm mt-1">Alle slots zijn mogelijk al bezet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {slots.map((slot, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedSlot(slot)}
                    disabled={!slot.available}
                    className={`p-3 text-center rounded-lg border transition-colors ${
                      !slot.available
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : selectedSlot?.startAt?.getTime() === slot.startAt.getTime()
                        ? 'bg-ios-blue text-white border-ios-blue'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {formatTime(slot.startAt)} - {formatTime(slot.endAt)}
                      </span>
                    </div>
                    {!slot.available && slot.reason && (
                      <div className="text-xs mt-1 text-gray-400">
                        {slot.reason}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            </div>
          )}

          {/* Co-Tenants Section */}
          {listing?.maxBewoners > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-2" />
                Mede-huurders toevoegen (optioneel)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Voeg vrienden toe die ook mee willen kijken. Maximaal {listing.maxBewoners - 1} mede-huurder(s).
              </p>
              
              {/* Add Co-Tenant */}
              <div className="flex gap-2 mb-3">
                <input
                  type="email"
                  value={coTenantEmail}
                  onChange={(e) => setCoTenantEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      searchCoTenantByEmail(coTenantEmail);
                    }
                  }}
                  placeholder="email@voorbeeld.nl"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ios-blue focus:border-transparent"
                  disabled={searchingCoTenant || coTenants.length >= (listing.maxBewoners - 1)}
                />
                <button
                  type="button"
                  onClick={() => searchCoTenantByEmail(coTenantEmail)}
                  disabled={searchingCoTenant || !coTenantEmail || coTenants.length >= (listing.maxBewoners - 1)}
                  className="px-4 py-2 bg-ios-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <UserPlus className="w-4 h-4" />
                  <span>{searchingCoTenant ? 'Zoeken...' : 'Toevoegen'}</span>
                </button>
              </div>

              {/* Co-Tenants List */}
              {coTenants.length > 0 && (
                <div className="space-y-2">
                  {coTenants.map((coTenant) => (
                    <div
                      key={coTenant.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-ios-blue text-white flex items-center justify-center font-semibold">
                          {coTenant.displayName?.charAt(0)?.toUpperCase() || coTenant.email?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{coTenant.displayName}</p>
                          <p className="text-xs text-gray-500">{coTenant.email}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCoTenant(coTenant.id)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Opmerkingen (optioneel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="input-field"
              placeholder="Bijvoorbeeld: verzamelpunt, speciale instructies..."
            />
          </div>

          {/* Selected Slot Summary */}
          {selectedSlot && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-blue-800">
                <Check className="w-5 h-5" />
                <span className="font-medium">Geselecteerd tijdstip:</span>
              </div>
              <p className="text-blue-700 mt-1">
                {formatDate(selectedSlot.startAt)} om {formatTime(selectedSlot.startAt)} - {formatTime(selectedSlot.endAt)}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={!selectedSlot || isSubmitting}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Aanvragen...</span>
                </div>
              ) : (
                'Bezichtiging aanvragen'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
